import { auth, db, googleProvider } from '../firebase/config';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    updateProfile,
    signInWithPopup
} from 'firebase/auth';
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    query,
    where,
    Timestamp,
    orderBy,
    setDoc,
    deleteField,
    increment
} from 'firebase/firestore';
import cityCoords from '../data/city_coords.json';

// Helper to clean user object
const cleanUser = (user, userData = {}) => {
    if (!user) return null;
    return {
        id: user.uid || user.id,
        uid: user.uid || user.id,
        email: user.email,
        displayName: user.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        photoURL: user.photoURL,
        phone: userData.phone || '',
        gender: userData.gender || '',
        level: userData.level || '',
        laterality: userData.laterality || '',
        position: userData.position || '',
        ...userData
    };
};

const mapDoc = (doc) => ({
    id: doc.id,
    ...doc.data()
});

// Robust normalization for city matching
const normalizeCity = (str) => {
    if (!str) return '';
    return str.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .replace(/[^a-z0-9]/g, "")        // remove all non-alphanumeric (hyphens, spaces, etc)
        .trim();
};

/**
 * Haversine formula to calculate distance between two points in KM
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// EVENTOS
export const getEvents = async (filters = {}) => {
    let q = collection(db, 'events');

    // Filter by organizer at database level if present
    if (filters.organizerId) {
        q = query(q, where('organizerId', '==', filters.organizerId));
    }

    const querySnapshot = await getDocs(q);
    let events = querySnapshot.docs.map(mapDoc);

    // Filter by Club
    if (filters.clubId) {
        events = events.filter(e => e.clubId === filters.clubId || e.club?.id === filters.clubId);
    } else if (filters.clubName) {
        // Nueva búsqueda por nombre de club (case insensitive)
        const normalize = (str) => str?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim() || '';
        const searchName = normalize(filters.clubName);
        events = events.filter(e => {
            const eventClubName = normalize(e.club?.name || e.clubName || '');
            return eventClubName.includes(searchName);
        });
    }

    // Filter by Date
    if (filters.date && filters.date !== 'all') {
        events = events.filter(e => e.date === filters.date);
    }

    // Filter by Level
    if (filters.level) {
        const parts = filters.level.split('-');
        if (parts.length === 2) {
             const filterMin = parseFloat(parts[0]);
             const filterMax = parseFloat(parts[1]);
             
             events = events.filter(e => {
                 const eventMin = parseFloat(e.levelMin);
                 const eventMax = parseFloat(e.levelMax);
                 return Math.max(filterMin, eventMin) < Math.min(filterMax, eventMax);
             });
        }
    }

    // New: Filter by Event Type
    if (filters.type && filters.type !== 'all') {
        events = events.filter(e => {
            // If searching for 'pozo', also include events where type is missing (legacy)
            if (filters.type === 'pozo' && !e.type) return true;
            return e.type === filters.type;
        });
    }

    // Filter by City
    if (filters.city) {
        const normalizedFilterCity = normalizeCity(filters.city);

        events = events.filter(e => {
            const eventCity = e.club?.city || e.city || '';
            const normalizedEventCity = normalizeCity(eventCity);
            // Match if one contains the other (e.g. "Rivas" in "Rivas-Vaciamadrid")
            return normalizedEventCity.includes(normalizedFilterCity) || normalizedFilterCity.includes(normalizedEventCity);
        });
    }

    // New: Filter by Distance
    if (filters.userCoords && filters.maxDistance) {
        const { lat, lon } = filters.userCoords;
        const maxDist = parseFloat(filters.maxDistance);

        events = events.map(e => {
            let eventLat = e.club?.latitude || e.latitude || e.club?.lat || e.lat;
            let eventLon = e.club?.longitude || e.longitude || e.club?.lon || e.lon;

            // Simple distance check
            const dist = calculateDistance(lat, lon, eventLat, eventLon);
            
            // Check if it's the same city (to bypass strict 5km limit if needed)
            const isSameCity = filters.city && normalizeCity(e.club?.city || e.city) === normalizeCity(filters.city);

            return { ...e, distance: dist, isSameCity };
        }).filter(e => {
            if (e.distance === null) return !!filters.city;
            // If it's the same city, we are more lenient with the 5km limit (allow up to 15km)
            // This handles cases where user is at one edge of town and city center is at the other
            const limit = e.isSameCity ? Math.max(maxDist, 15) : maxDist;
            return e.distance <= limit;
        });
    }

    // Sort in memory
    const sortedEvents = events.sort((a, b) => {
        if (filters.sort === 'distance') {
            const distA = a.distance === null ? Infinity : a.distance;
            const distB = b.distance === null ? Infinity : b.distance;
            return distA - distB;
        }
        if (filters.sort === 'price-asc') {
            return (a.pricePerPlayer || 0) - (b.pricePerPlayer || 0);
        }
        if (filters.sort === 'price-desc') {
            return (b.pricePerPlayer || 0) - (a.pricePerPlayer || 0);
        }
        if (filters.sort === 'date-desc') {
             return (b.date || '').localeCompare(a.date || '');
        }
        
        const dateA = a.date || '';
        const dateB = b.date || '';
        return dateA.localeCompare(dateB);
    });

    return {
        events: sortedEvents,
        pagination: {
            page: 1,
            limit: sortedEvents.length,
            total: sortedEvents.length,
            pages: 1
        }
    };
};

export const getEventById = async (id) => {
    const eventDoc = await getDoc(doc(db, 'events', id));
    if (!eventDoc.exists()) return null;
    return mapDoc(eventDoc);
};

export const getEvent = getEventById;

export const createEvent = async (eventData) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('Usuario no autenticado');
    }

    const docRef = await addDoc(collection(db, 'events'), {
        ...eventData,
        organizerId: eventData.organizerId || user.uid,
        createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...eventData, organizerId: eventData.organizerId || user.uid };
};

export const updateEvent = async (id, eventData) => {
    const eventRef = doc(db, 'events', id);
    // Ensure we don't accidentally overwrite with undefined
    const cleanData = {};
    Object.keys(eventData).forEach(key => {
        if (eventData[key] !== undefined) cleanData[key] = eventData[key];
    });
    
    await updateDoc(eventRef, {
        ...cleanData,
        updatedAt: Timestamp.now()
    });
    return { id, ...cleanData };
};

export const startEvent = async (id, config = {}) => {
    const eventRef = doc(db, 'events', id);
    const startData = {
        status: 'in_progress',
        actualStartTime: Timestamp.now(),
        // Default to time-based if duration is provided, else leader-court
        gameMode: config.gameMode || (config.matchDuration ? 'time' : 'leader_court'),
        matchDuration: config.matchDuration || 20, // default 20 min
        updatedAt: Timestamp.now(),
        ...config
    };
    await updateDoc(eventRef, startData);
    return { id, ...startData };
};

export const deleteEvent = async (id) => {
    await deleteDoc(doc(db, 'events', id));
    return id;
};

export const registerForEvent = async (eventId, registrationData) => {
    const status = registrationData.status || 'confirmed'; 

    // 1. Validar que el jugador no esté ya inscrito en este evento
    if (registrationData.player1Id) {
        const q = query(
            collection(db, 'registrations'), 
            where('eventId', '==', eventId),
            where('player1Id', '==', registrationData.player1Id)
        );
        const querySnapshot = await getDocs(q);
        const alreadyRegistered = querySnapshot.docs.some(doc => 
            ['confirmed', 'pending_payment'].includes(doc.data().status)
        );
        
        if (alreadyRegistered) {
            throw new Error('EL_JUGADOR_YA_ESTA_INSCRITO');
        }
    }

    // Fetch current user's name to store in registration
    // If player1Name is provided in registrationData (e.g. manual registration), use it
    let player1Name = registrationData.player1Name;
    
    if (!player1Name) {
        const user = auth.currentUser;
        player1Name = 'Jugador sin nombre';
        
        if (user) {
            // Try to get name from Firestore user profile first
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                player1Name = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || user.displayName || user.email || 'Jugador sin nombre';
            } else {
                // Fallback to Firebase Auth displayName
                player1Name = user.displayName || user.email || 'Jugador sin nombre';
            }
        }
    }

    const regRef = await addDoc(collection(db, 'registrations'), {
        ...registrationData,
        eventId,
        createdAt: Timestamp.now(),
        status: status,
        player1Name: player1Name, // Store player 1 name
        player2Name: registrationData.partnerName || null // Store partner name if provided
    });

    // If confirmed, update event pairs count
    if (status === 'confirmed') {
        const eventRef = doc(db, 'events', eventId);
        await updateDoc(eventRef, { currentPairs: increment(1) });
    }

    return { 
        registration: { 
            id: regRef.id, 
            ...registrationData, 
            status,
            player1Name,
            player2Name: registrationData.partnerName || null
        } 
    };
};

export const registerToEvent = registerForEvent;

export const unregisterFromEvent = async (eventIdOrRegistrationId) => {
    // Note: The previous implementation logic might have expected a registrationId.
    // BUT EventDetail calls it with eventId: `unregisterMutation.mutate() -> unregisterFromEvent(eventId)`.
    // The previous implementation tried to use it as registrationId: `doc(db, 'registrations', registrationId)`.
    // THIS WAS A BUG as well if called with eventId!
    
    // Now we standardizing: pass EVENT ID.
    // The Cloud Function finds the registration for the user + event.
    
    // If the argument is seemingly a registration ID (historic usage?), we might have an issue.
    // But since I'm fixing the main flow from EventDetail, I will assume it receives eventId.
    // Let's name it eventId to be clear.
    const eventId = eventIdOrRegistrationId; 
    
    const cancelFn = httpsCallable(functions, 'cancelRegistration');
    
    try {
        const result = await cancelFn({ eventId });
        return result.data;
    } catch (error) {
        console.error("Error calling cancelRegistration:", error);
        throw error;
    }
};

import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

export const confirmRegistration = async (eventId, data) => {
    // We use a Cloud Function to confirm to bypass complex security rules
    // and ensure atomic updates of event participant counts.
    const confirmFn = httpsCallable(functions, 'confirmRegistration');
    
    try {
        const result = await confirmFn({ 
            eventId, 
            paymentIntentId: data.paymentIntentId 
        });
        return result.data;
    } catch (error) {
        console.error("Error calling confirmRegistration:", error);
        throw error;
    }
};

export const getUserRegistrations = async (userId) => {
    const q = query(collection(db, 'registrations'), where('playerId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapDoc);
};

export const getEventRegistrations = async (eventId) => {
    const q = query(collection(db, 'registrations'), where('eventId', '==', eventId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapDoc);
};

export const deletePlayer = async (userId) => {
    await deleteDoc(doc(db, 'users', userId));
    return userId;
};

export const deleteRegistration = async (registrationId) => {
    // This function ONLY deletes the registration and updates the event count.
    // IT DOES NOT DELETE THE PLAYER PROFILE.
    const regRef = doc(db, 'registrations', registrationId);
    const regSnap = await getDoc(regRef);
    
    if (regSnap.exists()) {
        const data = regSnap.data();
        if (data.status === 'confirmed') {
            const eventRef = doc(db, 'events', data.eventId);
            await updateDoc(eventRef, { currentPairs: increment(-1) });
        }
    }
    
    await deleteDoc(regRef);
    return registrationId;
};

export const getOrganizerEvents = async (organizerId) => {
    const q = query(collection(db, 'events'), where('organizerId', '==', organizerId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapDoc);
};

export const getOrganizerBySlug = async (slug) => {
    try {
        const q = query(
            collection(db, 'users'),
            where("slug", "==", slug),
            where("role", "==", "organizer")
        );
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) return null;
        
        if (querySnapshot.size > 1) {
            console.warn(`⚠️ [api] Se han encontrado ${querySnapshot.size} organizadores con el slug "${slug}". Esto causará conflictos de datos.`, 
                querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
            );
        }

        return mapDoc(querySnapshot.docs[0]);
    } catch (error) {
        console.error("Error getting organizer by slug:", error);
        throw error;
    }
};

export const getOrganizers = async () => {
    try {
        const q = query(collection(db, 'users'), where("role", "==", "organizer"));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(mapDoc);
    } catch (error) {
        console.error("Error getting organizers:", error);
        return [];
    }
};

// Alias for clarity in Master Dashboard
export const getAllOrganizers = getOrganizers;

export const getOrganizerPlayers = async (organizerId) => {
    try {
        // 1. Get organizer's events (to map last events)
        const eventsQ = query(collection(db, 'events'), where('organizerId', '==', organizerId));
        const eventsSnapshot = await getDocs(eventsQ);
        const eventIds = eventsSnapshot.docs.map(doc => doc.id);

        // 2. Scan registrations to find players who participated in organizer's events
        const regsSnapshot = await getDocs(collection(db, 'registrations'));
        const playerIdsFromRegs = new Set();
        const playerLastEvent = {};

        regsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (eventIds.includes(String(data.eventId))) {
                const p1Id = data.player1Id;
                const p2Id = data.player2Id;
                
                if (p1Id) {
                    playerIdsFromRegs.add(p1Id);
                    const event = eventsSnapshot.docs.find(e => e.id === data.eventId);
                    if (event) playerLastEvent[p1Id] = event.data().title;
                }
                if (p2Id) {
                    playerIdsFromRegs.add(p2Id);
                    const event = eventsSnapshot.docs.find(e => e.id === data.eventId);
                    if (event) playerLastEvent[p2Id] = event.data().title;
                }
            }
        });

        // 3. Get User Profiles
        // Filter: Players from registrations OR players created by this organizer
        const usersSnapshot = await getDocs(collection(db, 'users'));
        
        const players = usersSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(user => {
                const isFromRegs = playerIdsFromRegs.has(user.id);
                const isCreatedByMe = user.createdByOrganizer === true && user.organizerId === organizerId;
                return isFromRegs || isCreatedByMe;
            })
            .map(user => ({
                ...user,
                lastEvent: playerLastEvent[user.id] || 'N/A'
            }));

        console.log("getOrganizerPlayers: Found", players.length, "players for organizer", organizerId);
        return { players };
    } catch (error) {
        console.error("Error getting organizer players:", error);
        return { players: [] };
    }
};

export const getClubs = async (filters = {}) => {
    // 1. If filtering by organizer, fetch from Firestore (User's created clubs)
    // IMPORTANT: Only filter by organizerId if it has a truthy value (not empty string)
    if (filters.organizerId) {
        let q = query(collection(db, 'clubs'), where('organizerId', '==', filters.organizerId));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ ...mapDoc(doc), source: 'firestore' }));
    }

    // 2. Fetch JSON Clubs (Public DB)
    const response = await fetch('/data/clubes.json');
    if (!response.ok) {
        throw new Error('No se pudo cargar la base de datos de clubes');
    }
    let jsonClubs = await response.json();
    // Map JSON clubs to match Firestore structure slightly better for UI
    jsonClubs = jsonClubs.map((c, index) => ({
        id: `json-${index}`, 
        name: c.n || c.name, 
        city: c.c || c.city, 
        address: c.d || c.address, 
        source: 'json',
        ...c 
    }));

    // 3. If Master Admin wants Everything (Firestore + JSON)
    if (filters.fetchAllFirestore) {
        const q = query(collection(db, 'clubs'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const firestoreClubs = querySnapshot.docs.map(doc => ({ ...mapDoc(doc), source: 'firestore' }));
        
        // Merge: Firestore clubs first, then JSON
        return [...firestoreClubs, ...jsonClubs];
    }
    
    // 4. Standard Public Search (JSON only for now, unless we want to mix db results too)
    let clubs = jsonClubs;

    // Helper function to extract city from address
    const extractCityFromAddress = (address) => {
        if (!address) return '';
        // City is usually at the end after a dash, like "- Madrid" or "- Barcelona"
        const match = address.match(/\s*-\s*([^(]+?)(?:\s*\(|$)/);
        return match ? match[1].trim() : '';
    };

    // Filter by City - ALWAYS apply if city is provided, regardless of coordinates
    if (filters.city) {
        const normalizedFilterCity = normalizeCity(filters.city);

        clubs = clubs.filter(c => {
            const cityValue = c.c || (c.d ? extractCityFromAddress(c.d) : '');
            const normalizedClubCity = normalizeCity(cityValue);
            return normalizedClubCity.includes(normalizedFilterCity) || normalizedFilterCity.includes(normalizedClubCity);
        });
    }

    // Filter by Distance - Simplified Logic
    if (filters.userCoords && filters.maxDistance) {
        const { lat, lon } = filters.userCoords;
        const maxDist = parseFloat(filters.maxDistance);

        clubs = clubs.map(c => {
            let dist = null;
            // Only calculate distance if club has coordinates
            if (c.lat !== null && c.lon !== null) {
                dist = calculateDistance(lat, lon, c.lat, c.lon);
            }
            return { ...c, distance: dist };
        }).filter(c => {
            // 1. If club has coordinates, apply strict distance filter
            if (c.distance !== null) {
                return c.distance <= maxDist;
            }
            
            // 2. If club has NO coordinates (legacy data), rely on the City Filter
            // If the user searched for "Madrid", simple text matching (already done via filters.city or text search) is enough.
            // We DON'T filter these out based on distance, because we don't know where they are!
            // This ensures we always show clubs even if our data is incomplete.
            return true;
        });
    }

    // Apply Sorting
    if (filters.sort === 'name-asc') {
        clubs.sort((a, b) => (a.n || a.name || '').localeCompare(b.n || b.name || ''));
    } else if (filters.sort === 'name-desc') {
        clubs.sort((a, b) => (b.n || b.name || '').localeCompare(a.n || a.name || ''));
    } else if (filters.sort === 'distance' && filters.userCoords) {
        clubs.sort((a, b) => {
            // Clubs with distance come first, then valid distances sorted, then nulls
            const distA = a.distance === null ? 999999 : a.distance;
            const distB = b.distance === null ? 999999 : b.distance;
            return distA - distB;
        });
    }

    return clubs;
};

export const getClubById = async (id) => {
    const clubDoc = await getDoc(doc(db, 'clubs', id));
    if (!clubDoc.exists()) return null;
    return mapDoc(clubDoc);
};

export const getClubBySlug = async (slug) => {
    const q = query(collection(db, 'clubs'), where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) return null;
    return mapDoc(querySnapshot.docs[0]);
};


export const createClub = async (clubData) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('Usuario no autenticado');
    }

    let lat = clubData.latitude || null;
    let lon = clubData.longitude || null;

    // Try to geocode address if provided and not already geocoded
    if (!lat && !lon && clubData.address && clubData.city) {
        try {
            const zipPart = clubData.postalCode ? `${clubData.postalCode}, ` : '';
            const query = encodeURIComponent(`${clubData.address}, ${zipPart}${clubData.city}, Spain`);
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`);
            const data = await response.json();
            if (data && data.length > 0) {
                lat = parseFloat(data[0].lat);
                lon = parseFloat(data[0].lon);
            }
        } catch (error) {
            console.error('Error geocoding club address:', error);
        }
    }

    const docRef = await addDoc(collection(db, 'clubs'), {
        ...clubData,
        latitude: lat,
        longitude: lon,
        organizerId: clubData.organizerId || user.uid,
        createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...clubData, latitude: lat, longitude: lon, organizerId: clubData.organizerId || user.uid };
};

export const updateClub = async (id, clubData) => {
    const clubRef = doc(db, 'clubs', id);
    
    let lat = clubData.latitude;
    let lon = clubData.longitude;

    // Try to re-geocode if address or city is being updated AND no coordinates were explicitly provided
    if (lat === undefined && lon === undefined && (clubData.address || clubData.city)) {
        try {
            // We might need to fetch the existing data to have a full address for geocoding
            // if only one of them (address OR city) is provided in the update.
            let fullAddress = clubData.address || '';
            let fullCity = clubData.city || '';
            let fullPostalCode = clubData.postalCode || '';

            if (!clubData.address || !clubData.city || !clubData.postalCode) {
                const clubSnap = await getDoc(clubRef);
                if (clubSnap.exists()) {
                    const currentData = clubSnap.data();
                    fullAddress = clubData.address || currentData.address || '';
                    fullCity = clubData.city || currentData.city || '';
                    fullPostalCode = clubData.postalCode || currentData.postalCode || '';
                }
            }

            if (fullAddress && fullCity) {
                const zipPart = fullPostalCode ? `${fullPostalCode}, ` : '';
                const query = encodeURIComponent(`${fullAddress}, ${zipPart}${fullCity}, Spain`);
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`);
                const data = await response.json();
                if (data && data.length > 0) {
                    lat = parseFloat(data[0].lat);
                    lon = parseFloat(data[0].lon);
                }
            }
        } catch (error) {
            console.error('Error re-geocoding club address:', error);
        }
    }

    const finalData = {
        ...clubData,
        updatedAt: Timestamp.now()
    };

    if (lat !== undefined && lat !== null) finalData.latitude = lat;
    if (lon !== undefined && lon !== null) finalData.longitude = lon;

    await updateDoc(clubRef, finalData);
    return { id, ...finalData };
};

export const deleteClub = async (id) => {
    await deleteDoc(doc(db, 'clubs', id));
    return id;
};

export const getEventMatches = async (eventId) => {
    const q = query(collection(db, 'matches'), where('eventId', '==', eventId));
    const querySnapshot = await getDocs(q);
    const matches = querySnapshot.docs.map(mapDoc);
    
    // Sort in memory to avoid needing a Firestore composite index
    return matches.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

export const createMatch = async (matchData) => {
    const docRef = await addDoc(collection(db, 'matches'), {
        ...matchData,
        status: matchData.status || 'scheduled',
        createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...matchData };
};

export const updateEventAssignments = async (eventId, assignments) => {
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, { assignments, lastAssignmentUpdate: Timestamp.now() });
    return { id: eventId, assignments };
};

export const startMatch = async (matchId) => {
    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, { status: 'live', startTime: Timestamp.now() });
    return { id: matchId, status: 'live' };
};

export const updateMatchScore = async (matchId, score1, score2) => {
    const matchRef = doc(db, 'matches', matchId);
    const updateData = {
        scorePair1: score1,
        scorePair2: score2,
        updatedAt: Timestamp.now()
    };
    await updateDoc(matchRef, updateData);
    return { id: matchId, ...updateData };
};

export const updateMatch = async (matchId, data) => {
    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, { ...data, updatedAt: Timestamp.now() });
    return { id: matchId, ...data };
};

export const finishMatch = async (matchId, result) => {
    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, { status: 'finished', result, endTime: Timestamp.now() });
    return { id: matchId, status: 'finished', result };
};

export const deleteMatch = async (matchId) => {
    await deleteDoc(doc(db, 'matches', matchId));
    return matchId;
};

export const getDashboardAnalytics = async (organizerId) => {
    try {
        // 1. Obtener todos los eventos del organizador
        const eventsQ = query(collection(db, 'events'), where('organizerId', '==', organizerId));
        const eventsSnapshot = await getDocs(eventsQ);
        const events = eventsSnapshot.docs.map(mapDoc);

        const totalEvents = events.length;
        const activeEvents = events.filter(e => e.status === 'published' || e.status === 'in_progress').length;

        // 2. Obtener inscripciones de esos eventos para calcular recaudación y jugadores únicos
        let totalRevenue = 0;
        const uniquePlayers = new Set();
        const eventIds = events.map(e => e.id);

        // Si hay eventos, buscamos sus inscripciones
        if (eventIds.length > 0) {
            // Nota: Firestore tiene límites en 'in' (máx 10). Para una escala real, esto debería ser diferente.
            // Para el MVP/SaaS inicial, buscaremos las inscripciones por lotes o una por una si es necesario.
            // Optamos por buscar todas las 'confirmed' y filtrar en memoria por simplicidad inicial.
            const regsSnapshot = await getDocs(collection(db, 'registrations'));
            const allRegs = regsSnapshot.docs.map(mapDoc);

            const organizerRegs = allRegs.filter(r => eventIds.includes(r.eventId) && r.status === 'confirmed');

            organizerRegs.forEach(reg => {
                const event = events.find(e => e.id === reg.eventId);
                if (event) {
                    totalRevenue += (parseFloat(event.pricePerPlayer) || 0);
                }
                if (reg.player1Id) uniquePlayers.add(reg.player1Id);
                if (reg.player2Id) uniquePlayers.add(reg.player2Id);
            });
        }

        return {
            analytics: {
                totalRevenue,
                totalPlayers: uniquePlayers.size,
                totalEvents,
                activeEvents
            }
        };
    } catch (error) {
        console.error('Error fetching dashboard analytics:', error);
        return {
            analytics: { totalRevenue: 0, totalPlayers: 0, totalEvents: 0, activeEvents: 0 }
        };
    }
};

export const getOrganizerCounts = async (organizerId) => {
    try {
        const [eventsSnap, clubsSnap, regsSnap] = await Promise.all([
            getDocs(query(collection(db, 'events'), where('organizerId', '==', organizerId))),
            getDocs(query(collection(db, 'clubs'), where('organizerId', '==', organizerId))),
            getDocs(collection(db, 'registrations'))
        ]);

        const eventIds = eventsSnap.docs.map(d => d.id);
        const uniquePlayers = new Set();

        regsSnap.docs.forEach(doc => {
            const data = doc.data();
            if (eventIds.includes(data.eventId) && data.status === 'confirmed') {
                if (data.player1Id) uniquePlayers.add(data.player1Id);
                if (data.player2Id) uniquePlayers.add(data.player2Id);
            }
        });

        return {
            events: eventsSnap.size,
            clubs: clubsSnap.size,
            players: uniquePlayers.size
        };
    } catch (error) {
        console.error('Error fetching counts:', error);
        return { events: 0, clubs: 0, players: 0 };
    }
};

// USUARIOS & AUTH
export const getCurrentUser = async () => {
    const user = auth.currentUser;

    if (!user) {
        console.log('api.getCurrentUser: No authenticated user found');
        return null;
    }

    console.log('api.getCurrentUser: Found user with uid=', user.uid);

    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userDocRef);

    const userData = userDocSnap.exists() ? userDocSnap.data() : {};

    return cleanUser(user, userData);
};

export const updateUserProfile = async (userId, profileData) => {
    console.log('api.updateUserProfile: Attempting update for', userId, profileData);
    const userRef = doc(db, 'users', userId);
    
    // Solo actualizamos campos no nulos
    const cleanData = {};
    Object.keys(profileData).forEach(key => {
        if (profileData[key] !== undefined) cleanData[key] = profileData[key];
    });

    await setDoc(userRef, { ...cleanData, updatedAt: Timestamp.now() }, { merge: true });
    console.log('api.updateUserProfile: Success');
    return { id: userId, ...cleanData };
};

export const createPlayerByOrganizer = async (playerData) => {
    // 1. Verificar si el email ya existe en el SaaS general
    if (playerData.email) {
        const q = query(collection(db, 'users'), where('email', '==', playerData.email));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            const existingUser = querySnapshot.docs[0];
            console.log("createPlayerByOrganizer: Jugador ya existe en SaaS general:", existingUser.id);
            return { id: existingUser.id, ...existingUser.data(), alreadyExisted: true };
        }
    }

    // 2. Si no existe, creamos el documento en 'users' (Base general del SaaS)
    const docRef = await addDoc(collection(db, 'users'), {
        ...playerData,
        role: 'player',
        createdByOrganizer: true,
        createdAt: Timestamp.now()
    });
    return { id: docRef.id, ...playerData };
};

export const changePassword = async () => {
    console.warn("changePassword handled by Firebase Auth");
    return { success: true };
}

export const login = async ({ email, password }) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        const userData = userDocSnap.exists() ? userDocSnap.data() : { role: 'player' };
        const token = await user.getIdToken();

        localStorage.setItem('token', token);
        localStorage.setItem('userId', user.uid);
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('userRole', userData.role || 'player');
        localStorage.setItem('userName', `${userData.firstName || ''} ${userData.lastName || ''}`);

        if (userData.disabled) {
            await signOut(auth);
            throw new Error('Tu cuenta ha sido suspendida. Contacta con el administrador.');
        }

        return { user: cleanUser(user, userData), token };
    } catch (error) {
        console.error("Error logging in:", error);
        throw error;
    }
};

export const signup = async ({ 
    email, 
    password, 
    firstName, 
    lastName, 
    role = 'player', 
    phone = '', 
    gender = '', 
    level = '',
    laterality = '',
    position = ''
}) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await updateProfile(user, { displayName: `${firstName} ${lastName}` });
        const userData = { 
            firstName, 
            lastName, 
            email, 
            role, 
            phone, 
            gender, 
            level,
            laterality,
            position,
            createdAt: Timestamp.now() 
        };
        await setDoc(doc(db, 'users', user.uid), userData);
        const token = await user.getIdToken();

        localStorage.setItem('token', token);
        localStorage.setItem('userId', user.uid);
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('userRole', role);
        localStorage.setItem('userName', `${firstName} ${lastName}`);

        return { user: cleanUser(user, userData), token };
    } catch (error) {
        console.error("Error signing up:", error);
        throw error;
    }
};

export const logout = async () => {
    try {
        await signOut(auth);
        localStorage.clear();
        window.location.href = '/login';
    } catch (error) {
        console.error("Error signing out:", error);
    }
};

export const googleSignIn = async (role = 'player') => {
    try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        let userData;
        if (userDocSnap.exists()) {
            userData = userDocSnap.data();
        } else {
            const nameParts = user.displayName ? user.displayName.split(' ') : ['Google', 'User'];
            userData = {
                firstName: nameParts[0],
                lastName: nameParts.slice(1).join(' ') || '',
                email: user.email,
                role: role,
                createdAt: Timestamp.now()
            };
            await setDoc(userDocRef, userData);
        }
        const token = await user.getIdToken();
        localStorage.setItem('token', token);
        localStorage.setItem('userId', user.uid);
        localStorage.setItem('userEmail', user.email);
        localStorage.setItem('userRole', userData.role || 'player');
        localStorage.setItem('userName', user.displayName || 'Usuario');

        if (userData.disabled) {
            await signOut(auth);
            throw new Error('Tu cuenta ha sido suspendida. Contacta con el administrador.');
        }

        return { user: cleanUser(user, userData), token };
    } catch (error) {
        console.error("Error with Google sign in:", error);
        throw error;
    }
};

const api = {
    getEvents, getEventById, getEvent, createEvent, updateEvent, deleteEvent,
    registerForEvent, registerToEvent, unregisterFromEvent, confirmRegistration,
    getUserRegistrations, getEventRegistrations, getOrganizerEvents, getOrganizerBySlug,
    getOrganizers, getOrganizerPlayers, getClubs, getClubById, getClubBySlug,
    createClub, updateClub, deleteClub, getEventMatches, startMatch, finishMatch, createMatch,
    updateEventAssignments, startEvent, updateMatchScore,
    getDashboardAnalytics, getCurrentUser, updateUserProfile, changePassword,
    login, signup, logout, googleSignIn, createPlayerByOrganizer
};

// --- SUPER ADMIN GLOBAL FETCHERS ---

/**
 * getAllOrganizers - Fetches all users with role 'organizer'
 */


/**
 * getAllUsersGlobal - Fetches all users (players and organizers)
 */
export const getAllUsersGlobal = async () => {
    try {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(mapDoc);
    } catch (error) {
        console.error("Error fetching all users:", error);
        throw error;
    }
};

/**
 * getAllEventsGlobal - Fetches all events from all organizers
 */
export const getAllEventsGlobal = async () => {
    try {
        const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(mapDoc);
    } catch (error) {
        console.error("Error fetching all events global:", error);
        throw error;
    }
};

/**
 * updateUserRole - Changes a user's role (e.g., to promote to organizer or super_admin)
 */
export const updateUserRole = async (userId, newRole) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { 
            role: newRole,
            updatedAt: Timestamp.now()
        });
        return { id: userId, role: newRole };
    } catch (error) {
        console.error("Error updating user role:", error);
        throw error;
    }
};

/**
 * updateUser - Updates any field of a user document
 */
export const updateUser = async (userId, data) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { 
            ...data,
            updatedAt: Timestamp.now()
        });
        return { id: userId, ...data };
    } catch (error) {
        console.error("Error updating user:", error);
        throw error;
    }
};

export const getUser = async (userId) => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
            return { id: userDoc.id, ...userDoc.data() };
        }
        return null;
    } catch (error) {
        console.error("Error fetching user:", error);
        throw error;
    }
};

/**
 * createUserDoc - Creates a user document in Firestore manually (admin only)
 * Note: This does NOT create a Firebase Auth user, just the Firestore record.
 */
export const createUserDoc = async (userData) => {
    try {
        // Use email as ID if no auth UID is available yet, or generate one
        const newRef = doc(collection(db, 'users'));
        await setDoc(newRef, {
            ...userData,
            createdAt: Timestamp.now(),
            role: userData.role || 'organizer',
            disabled: false
        });
        return { id: newRef.id, ...userData };
    } catch (error) {
        console.error("Error creating user doc:", error);
        throw error;
    }
};

export const checkSlugUniqueness = async (slug, currentUserId = null) => {
    try {
        const q = query(
            collection(db, 'users'),
            where("slug", "==", slug)
        );
        const querySnapshot = await getDocs(q);
        
        const conflict = querySnapshot.docs.find(doc => doc.id !== currentUserId);
        return !conflict;
    } catch (error) {
        console.error("Error checking slug uniqueness:", error);
        throw error;
    }
};

export const cleanupDuplicateSlugs = async (slug, keepUserId) => {
    try {
        const q = query(
            collection(db, 'users'),
            where("slug", "==", slug)
        );
        const querySnapshot = await getDocs(q);
        
        const updates = [];
        querySnapshot.forEach((docSnap) => {
            if (docSnap.id !== keepUserId) {
                console.log(`🧹 [api] Limpiando slug duplicado en usuario: ${docSnap.id}`);
                const userRef = doc(db, 'users', docSnap.id);
                // Eliminamos el campo slug de los duplicados
                updates.push(updateDoc(userRef, { 
                    slug: deleteField(),
                    updatedAt: Timestamp.now()
                }));
            }
        });
        
        await Promise.all(updates);
        return updates.length;
    } catch (error) {
        console.error("Error cleaning up duplicate slugs:", error);
        throw error;
    }
};

export default api;
